import React from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { NumberField } from "@/components/simulator/NumberField";
import { fmtEUR, fmtNum } from "@/lib/format";
import { Flower2, Sparkles } from "lucide-react";

export const SaffronTab = () => {
  const { inputs, summary, updateSaffron } = useSimulator();
  const s = inputs.saffron;
  const r = summary.saffron;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3">
            <Flower2 className="h-7 w-7 text-amber-400" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-100">
              Lotto 1 — Zafferano Aeroponico
            </h2>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Coltivazione di <em>Crocus sativus</em> su rack verticali in
              ambiente controllato. Ripartizione produttiva su tre forme di
              prodotto ad alto valore aggiunto.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-900/70 border-slate-800 p-6 space-y-6">
          <div className="flex items-center gap-2 text-amber-400">
            <Sparkles className="h-4 w-4" />
            <h3 className="font-display text-lg font-semibold">Parametri produttivi</h3>
          </div>

          <NumberField
            label="Superficie serra"
            value={s.surface_m2}
            onChange={(v) => {
              const kg = (v * s.levels * s.yield_g_m2) / 1000;
              updateSaffron({ surface_m2: v, kg_year: Number(kg.toFixed(2)) });
            }}
            min={100}
            max={1000}
            step={10}
            unit="m²"
            testId="saffron-surface"
            sliderTheme="saffron-slider"
            hint="Config preferita H2 Farm: 300 m² (confronto con 250 e 350)"
          />

          <div className="grid grid-cols-2 gap-3">
            <NumberField
              label="Livelli rack"
              value={s.levels}
              onChange={(v) => {
                const kg = (s.surface_m2 * v * s.yield_g_m2) / 1000;
                updateSaffron({ levels: v, kg_year: Number(kg.toFixed(2)) });
              }}
              min={1}
              max={10}
              step={1}
              unit=""
              testId="saffron-levels"
              sliderTheme="saffron-slider"
              hint={`superficie eq.: ${(s.surface_m2 * s.levels).toLocaleString("it-IT")} m²`}
            />
            <NumberField
              label="Resa per m² equivalente"
              value={s.yield_g_m2}
              onChange={(v) => {
                const kg = (s.surface_m2 * s.levels * v) / 1000;
                updateSaffron({ yield_g_m2: v, kg_year: Number(kg.toFixed(2)) });
              }}
              min={1}
              max={8}
              step={0.1}
              unit="g / m²"
              testId="saffron-yield-g-m2"
              sliderTheme="saffron-slider"
              hint="3,2 prudente · 4,0 base · 5,6 max"
            />
          </div>

          <NumberField
            label="Produzione annua stigmi (GMP)"
            value={s.kg_year}
            onChange={(v) => updateSaffron({ kg_year: v })}
            min={0.5}
            max={50}
            step={0.1}
            unit="kg / anno"
            testId="saffron-kg"
            sliderTheme="saffron-slider"
            hint={`derivato: ${s.surface_m2} × ${s.levels} × ${s.yield_g_m2} g/m² / 1000`}
          />

          <div className="pt-2 border-t border-slate-800/80" />

          <NumberField
            label="Prezzo stimmi essiccati"
            value={s.price_dried}
            onChange={(v) => updateSaffron({ price_dried: v })}
            min={500}
            max={15000}
            step={50}
            unit="€ / kg"
            testId="saffron-price-dried"
            sliderTheme="saffron-slider"
            hint="Wholesale premium EU: €3,25/g – €7,5/g"
          />

          <NumberField
            label="Prezzo estratto standardizzato"
            value={s.price_extract}
            onChange={(v) => updateSaffron({ price_extract: v })}
            min={2000}
            max={50000}
            step={250}
            unit="€ / kg"
            testId="saffron-price-extract"
            sliderTheme="saffron-slider"
          />

          <NumberField
            label="Prezzo estratto SC-CO₂"
            value={s.price_scco2}
            onChange={(v) => updateSaffron({ price_scco2: v })}
            min={5000}
            max={80000}
            step={500}
            unit="€ / kg"
            testId="saffron-price-scco2"
            sliderTheme="saffron-slider"
          />

          <div className="pt-4 border-t border-slate-800/80 space-y-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-amber-300/80">
              Rese di estrazione
            </div>
            <NumberField
              label="Resa estratto standardizzato"
              value={s.yield_extract}
              onChange={(v) => updateSaffron({ yield_extract: v })}
              min={5}
              max={40}
              step={0.5}
              unit="%"
              testId="saffron-yield-extract"
              sliderTheme="saffron-slider"
              hint="kg estratto ottenuti per 1 kg stigma trasformata"
            />
            <NumberField
              label="Resa estratto SC-CO₂"
              value={s.yield_scco2}
              onChange={(v) => updateSaffron({ yield_scco2: v })}
              min={2}
              max={25}
              step={0.5}
              unit="%"
              testId="saffron-yield-scco2"
              sliderTheme="saffron-slider"
              hint="Confermata: 10%"
            />
          </div>
        </Card>

        <Card className="bg-slate-900/70 border-slate-800 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-slate-100">
              Ripartizione forme di prodotto
            </h3>
            <span className="text-xs font-mono-tab text-slate-500">
              somma {fmtNum(s.mix_dried + s.mix_extract + s.mix_scco2)}%
            </span>
          </div>

          <NumberField
            label="% stimmi essiccati"
            value={s.mix_dried}
            onChange={(v) => updateSaffron({ mix_dried: v })}
            min={0}
            max={100}
            step={1}
            unit="%"
            testId="saffron-mix-dried"
            sliderTheme="saffron-slider"
            hint={`stigma allocata: ${fmtNum(r.raw_dried, 2)} kg → ${fmtNum(r.kg_dried, 2)} kg vendibili`}
          />
          <NumberField
            label="% estratto standardizzato"
            value={s.mix_extract}
            onChange={(v) => updateSaffron({ mix_extract: v })}
            min={0}
            max={100}
            step={1}
            unit="%"
            testId="saffron-mix-extract"
            sliderTheme="saffron-slider"
            hint={`stigma allocata: ${fmtNum(r.raw_extract, 2)} kg → ${fmtNum(r.kg_extract, 3)} kg estratto (resa ${s.yield_extract}%)`}
          />
          <NumberField
            label="% estratto SC-CO₂"
            value={s.mix_scco2}
            onChange={(v) => updateSaffron({ mix_scco2: v })}
            min={0}
            max={100}
            step={1}
            unit="%"
            testId="saffron-mix-scco2"
            sliderTheme="saffron-slider"
            hint={`stigma allocata: ${fmtNum(r.raw_scco2, 2)} kg → ${fmtNum(r.kg_scco2, 3)} kg estratto (resa ${s.yield_scco2}%)`}
          />
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <RevCell label="Stimmi essiccati" value={r.rev_dried} kg={r.kg_dried} testId="saffron-rev-dried" />
        <RevCell label="Estratto standardizzato" value={r.rev_extract} kg={r.kg_extract} testId="saffron-rev-extract" />
        <RevCell label="Estratto SC-CO₂" value={r.rev_scco2} kg={r.kg_scco2} testId="saffron-rev-scco2" />
        <Card
          data-testid="saffron-revenue-total-display"
          className="bg-gradient-to-br from-amber-500/15 to-amber-600/5 border border-amber-500/30 p-5 flex flex-col justify-between"
        >
          <div className="text-xs font-semibold tracking-wider uppercase text-amber-300">
            Totale Lotto 1
          </div>
          <div className="mt-2 font-display font-extrabold text-2xl sm:text-3xl text-amber-300 font-mono-tab">
            {fmtEUR(r.total)}
          </div>
        </Card>
      </div>
    </div>
  );
};

const RevCell = ({ label, value, kg, testId }) => (
  <Card data-testid={testId} className="bg-slate-900/70 border-slate-800 p-5">
    <div className="text-xs font-semibold tracking-wider uppercase text-slate-400">
      {label}
    </div>
    <div className="mt-2 font-display font-bold text-xl text-slate-100 font-mono-tab">
      {fmtEUR(value)}
    </div>
    <div className="mt-1 text-[11px] text-slate-500 font-mono-tab">
      {fmtNum(kg, 3)} kg
    </div>
  </Card>
);
